import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import TypeNumber from '../../form/form-fields/TypeNumber';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../label/MoneyLabel';
import ExoDialog from '../ExoDialog';
import ExoForm from '../ExoForm';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { updateOrCreate } from '../../special/updateOrCreate';
import {
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../api/api';
import * as Yup from 'yup';
import Service from './Service';
import EmployeeClass from './EmployeeClass';
import { ProjectAddElementButton } from '../project/ProjectAddElementButton';
import uuid from 'react-uuid';
import { ExoVariants } from '../ExoVariants';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { EmployeeClassSelector } from './EmployeeClassSelector';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import TypeText from '../../form/form-fields/TypeText';
import BallotIcon from '@mui/icons-material/Ballot';
import { ServiceSelector } from './ServiceSelector';

//Employee Presets and Phases/Services are saved in their respective dataCollections
/* e.g. {
  servicesDataCollection = {services: [{service}, {service2}, ...]}
  empClassesDataCollection = {employeeClasses: [{emplyeeClass}, ...]}
}*/

export const WorkHourEstimator = ({
  project,
  startDataCollection,
  onSubmit = () => {},
  onCancel,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const [openNewEmpClass, setOpenNewEmpClass] = useState(null);
  const [openNewService, setOpenNewService] = useState(null);

  const newEmpFields = [
    { label: t('Title'), key: 'label', type: 'text' },
    { label: t('Hourly Rate'), key: 'wage', type: 'number', unit: '€' },
    {
      label: t('Employees'),
      key: 'users',
      type: 'users',
      options: project.teamMembers,
    },
    { label: t('Color'), key: 'color', type: 'color' },
    { label: t('Icon'), key: 'image', type: 'icon' },
  ];

  const newServiceFields = [
    { label: t('Title'), key: 'label', type: 'text' },

    {
      key: 'useAbsolute',
      type: 'toggelFieldsViaIcons',
      cards: [
        {
          label: t('Relative costs'),
          value: '0',
          formFields: [
            {
              label: t('Total value share'),
              key: 'percentOfHonorarium',
              type: 'number',
              unit: '%',
            },
          ],
        },
        {
          label: t('Absolute costs'),
          value: '1',
          formFields: [
            {
              label: t('Absolute Costs'),
              key: 'amount',
              type: 'number',
              unit: '€',
            },
          ],
        },
      ],
    },
    {
      label: t('Proportion of personnel costs'),
      key: 'personelCostOfPhasePercent',
      type: 'number',
      unit: '%',
    },
  ];

  const newEmpValidationSchema = Yup.object().shape({
    label: Yup.string().required('Title is required'),
    wage: Yup.number()
      .typeError('Salary must be a number')
      .required('Salary is required'),
    color: Yup.string()
      .matches(/^#([0-9A-F]{3}){1,2}$/i, 'Color must be a valid hex code')
      .required('Color is required'),
    //Image: Yup.mixed().notRequired(),
  });

  const newServiceValidationSchema = Yup.object().shape({
    label: Yup.string().required('Title is required'),

    personelCostOfPhasePercent: Yup.number()
      .typeError('Proportion of personnel costs must be a number')
      .required('Proportion of personnel costs is required'),

    percentOfHonorarium: Yup.number().when('useAbsolute', {
      is: (val) => val == '0',
      then: (schema) => schema.required('percentOfHonorarium is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    amount: Yup.number().when('useAbsolute', {
      is: (val) => val == '1',
      then: (schema) => schema.required('amount is required'),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const [dataCollection, setDataCollection] = useState({ data: {} });
  const [empClassesDataCollection, setEmpClassesDataCollection] = useState({
    employeeClasses: [],
  });
  const [servicesDataCollection, setServicesDataCollection] = useState({
    services: [],
  });

  function preloadEmpClasses() {
    /*
    const projectUserIds = project.teamMembers.map((el) => el.id);
    const path = projectUserIds.reduce((acc, userId) => {
      acc += '&user[]=' + userId;
      return acc;
    }, 'wage-presets?');*/

    const path = `wage-presets?projectId[eq]=${project.id}`;

    DBRequest({
      config,
      path: path,
      method: API_GET,
      onResponse: (res) =>
        setEmpClassesDataCollection({ employeeClasses: res.data }),
    });
  }

  useEffect(() => {
    if (!startDataCollection) {
      preloadEmpClasses();
      return;
    }

    const { services, wagePresets, ...newDataCollection } = startDataCollection;
    const newEmpClassesDataCollection = {
      employeeClasses: startDataCollection.wagePresets,
    };
    const newServicesDataCollection = {
      services: startDataCollection.services || [],
    };

    calculate(
      newDataCollection,
      newEmpClassesDataCollection,
      newServicesDataCollection
    );
  }, [startDataCollection, config]);

  function handleNettoHonorariumChange(key, newNettoHonorarium) {
    setDataCollection({
      ...dataCollection,
      totalCost: newNettoHonorarium,
    });
    calculate(
      {
        ...dataCollection,
        totalCost: newNettoHonorarium,
      },
      empClassesDataCollection,
      servicesDataCollection
    );
  }

  function handleEmployeePercentPerPhaseChange(
    key,
    newValue,
    service,
    empClass
  ) {
    let newData = dataCollection.data;

    newData[`emp${empClass.id}serv${service.id}`].employeePercentPerPhase =
      newValue;
    setDataCollection({ ...dataCollection, data: newData });
    calculate(
      { ...dataCollection, data: newData },
      empClassesDataCollection,
      servicesDataCollection
    );
  }

  function calculate(
    dataCollection,
    empClassesDataCollection,
    servicesDataCollection
  ) {
    if (
      !dataCollection?.data ||
      !empClassesDataCollection?.employeeClasses ||
      !servicesDataCollection?.services
    )
      return;
    let newDataCollection = dataCollection;
    let newEmpClassesDataCollection = empClassesDataCollection;
    let newServicesDataCollection = servicesDataCollection;

    const totalAbsoluteAmount = newServicesDataCollection.services.reduce(
      (acc, service) => {
        if (service.useAbsolute == '1') {
          acc += Number(service.amount);
        }
        return acc;
      },
      0
    );

    newServicesDataCollection.services.map((service) => {
      if (service.useAbsolute == '0') {
        service.amount = newDataCollection.totalCost
          ? Math.round(
              100 *
                (newDataCollection.totalCost - totalAbsoluteAmount) *
                (service.percentOfHonorarium / 100)
            ) / 100
          : 0;
      }

      if (!newDataCollection.totalCost) newDataCollection.totalCost = 0;

      service.personelCostOfPhasePercentAmount =
        Math.round(
          100 * service.amount * (service.personelCostOfPhasePercent / 100)
        ) / 100;

      service.percentSum = newEmpClassesDataCollection.employeeClasses.reduce(
        (acc, empClass) => {
          acc += Number(
            newDataCollection.data[`emp${empClass.id}serv${service.id}`]
              .employeePercentPerPhase
          );
          return acc;
        },
        0
      );
    });

    newEmpClassesDataCollection.employeeClasses.map((empClass) => {
      let empSumHonorariumShare = 0;
      let empSumHourShare = 0;
      newServicesDataCollection.services.map((service) => {
        let currentData =
          newDataCollection.data[`emp${empClass.id}serv${service.id}`];
        currentData.honorariumShare =
          Math.round(
            100 *
              service.personelCostOfPhasePercentAmount *
              (currentData.employeePercentPerPhase / 100)
          ) / 100;
        empSumHonorariumShare += currentData.honorariumShare;

        currentData.hourShare =
          Math.round((100 * currentData.honorariumShare) / empClass.wage) / 100;
        empSumHourShare += currentData.hourShare;

        newDataCollection.data[`emp${empClass.id}serv${service.id}`] =
          currentData;
      });

      empClass.empSumHonorariumShare = empSumHonorariumShare;
      empClass.empSumHourShare = empSumHourShare;
    });

    setDataCollection(newDataCollection);
    setEmpClassesDataCollection(newEmpClassesDataCollection);
    setServicesDataCollection(newServicesDataCollection);
  }

  function handleAddEmpClass(newEmp) {
    const projectIds = [...(newEmp.projectIds ?? []), project.id];
    const empData = { ...newEmp, projectIds: projectIds };

    DBRequest({
      config,
      path: Boolean(empData.id) ? `wage-presets/${empData.id}` : `wage-presets`,
      method: Boolean(empData.id) ? API_PATCH(empData) : API_POST(empData),
      onResponse: handleAddEmpClassResponse,
    });
  }

  function handleAddEmpClassResponse(res) {
    let newEmployeeClasses = empClassesDataCollection.employeeClasses;
    const newEmp = res.data;

    const exists = newEmployeeClasses.find((el) => el.id == newEmp.id);

    if (!exists) {
      newEmployeeClasses.push(newEmp);

      let newDataCollectionData = dataCollection.data;
      servicesDataCollection.services.forEach(
        (service) =>
          (newDataCollectionData[`emp${newEmp.id}serv${service.id}`] = {
            employeePercentPerPhase: 0,
          })
      );

      calculate(
        { ...dataCollection, data: newDataCollectionData },
        {
          ...empClassesDataCollection,
          employeeClasses: newEmployeeClasses,
        },
        servicesDataCollection
      );
    } else {
      newEmployeeClasses = updateOrCreate(
        newEmployeeClasses,
        newEmp,
        false,
        'id'
      );

      calculate(
        dataCollection,
        {
          ...empClassesDataCollection,
          employeeClasses: newEmployeeClasses,
        },
        servicesDataCollection
      );
    }

    setOpenNewEmpClass(null);
  }

  function handleDeleteEmpClass(empClass) {
    let newEmpClasses = empClassesDataCollection.employeeClasses.filter(
      (el) => el.id != empClass.id
    );

    let newData = dataCollection.data;
    servicesDataCollection.services.forEach(
      (service) => (newData[`emp${empClass.id}serv${service.id}`] = null)
    );

    calculate(
      { ...dataCollection, data: newData },
      {
        ...empClassesDataCollection,
        employeeClasses: newEmpClasses,
      },
      servicesDataCollection
    );
  }

  function handleAddService(newService) {
    let newServices = servicesDataCollection.services;

    if (newService.id) {
      newServices = updateOrCreate(newServices, newService, false, 'id');

      calculate(dataCollection, empClassesDataCollection, {
        ...servicesDataCollection,
        services: newServices,
      });
    } else {
      const id = uuid().replaceAll('-', '');

      newServices.push({ ...newService, id: id, expanded: false });

      let newDataCollectionData = dataCollection.data;
      empClassesDataCollection.employeeClasses.forEach(
        (empClass) =>
          (newDataCollectionData[`emp${empClass.id}serv${id}`] = {
            employeePercentPerPhase: 0,
          })
      );

      calculate(
        { ...dataCollection, data: newDataCollectionData },
        empClassesDataCollection,
        { ...servicesDataCollection, services: newServices }
      );
    }
    setOpenNewService(null);
  }

  function handleDeleteService(service) {
    let newServices = servicesDataCollection.services.filter(
      (el) => el.id != service.id
    );

    let newData = dataCollection.data;
    empClassesDataCollection.employeeClasses.forEach(
      (empClass) => (newData[`emp${empClass.id}serv${service.id}`] = null)
    );

    calculate(
      { ...dataCollection, data: newData },
      empClassesDataCollection,

      { ...servicesDataCollection, services: newServices }
    );
  }

  function generateEmpClassActions(empClass) {
    const empClassActions = [
      {
        icon: <EditIcon />,
        name: t('Edit'),
        onClick: () => setOpenNewEmpClass(empClass),
      },
      {
        icon: <RemoveCircleOutlineIcon />,
        name: t('Remove'),
        onClick: () => handleDeleteEmpClass(empClass),
      },
    ];

    return empClassActions;
  }

  function generateServiceActions(service) {
    const empClassActions = [
      {
        icon: <EditIcon />,
        name: t('Edit'),
        onClick: () => {
          setOpenNewService(service);
        },
      },
      {
        icon: <DeleteIcon />,
        name: t('Delete'),
        onClick: () => handleDeleteService(service),
      },
    ];

    return empClassActions;
  }

  function handleToggleExpand(service) {
    const newService = { ...service, expanded: !service.expanded };
    handleAddService(newService);
  }

  const newEmpClassVariants = [
    {
      label: t('Load Calculative Hourly Rate'),
      Icon: FileDownloadIcon,
      content: (
        <EmployeeClassSelector
          onSelect={(res) => handleAddEmpClass(res.data)}
          existingEmpClasses={empClassesDataCollection.employeeClasses}
        />
      ),
    },
    {
      label: t('New Calculative Hourly Rate'),
      Icon: AddCircleOutlineIcon,
      content: (
        <ExoForm
          fields={newEmpFields}
          dataCollection={openNewEmpClass}
          startDataCollection={openNewEmpClass}
          onSubmit={handleAddEmpClass}
          onCancel={() => setOpenNewEmpClass(null)}
          noDifferences={true}
          validationSchema={newEmpValidationSchema}
        />
      ),
    },
  ];

  const newServiceVariants = [
    {
      label: t('Add Service from Order'),
      Icon: BallotIcon,
      content: (
        <ServiceSelector
          project={project}
          onClose={() => setOpenNewService(null)}
          onSubmit={handleNewServicesFromOrder}
        />
      ),
    },
    {
      label: t('Add new Service'),
      Icon: AddCircleOutlineIcon,
      content: (
        <ExoForm
          fields={newServiceFields}
          dataCollection={openNewService}
          startDataCollection={{
            ...openNewService,
            useAbsolute: openNewService?.useAbsolute || '0',
            percentOfHonorarium: openNewService?.percentOfHonorarium || null,
            amount: openNewService?.amount || null,
          }}
          onSubmit={handleAddService}
          onCancel={() => {
            setOpenNewService(null);
          }}
          noDifferences={true}
          validationSchema={newServiceValidationSchema}
        />
      ),
    },
  ];

  function handleNewServicesFromOrder(positions) {
    let newServices = servicesDataCollection.services;
    let newDataCollectionData = dataCollection.data;
    positions.forEach((pos) => {
      if (!pos.isGroup) {
        const id = uuid().replaceAll('-', '');

        newServices.push({
          id: id,
          label: pos.name,
          amount: pos.amount * pos.price,
          personelCostOfPhasePercent: 100,
          useAbsolute: '1',
          expanded: true,
        });
        empClassesDataCollection.employeeClasses.forEach(
          (empClass) =>
            (newDataCollectionData[`emp${empClass.id}serv${id}`] = {
              employeePercentPerPhase: 0,
            })
        );
      }
    });

    calculate(
      { ...dataCollection, data: newDataCollectionData },
      empClassesDataCollection,
      { ...servicesDataCollection, services: newServices }
    );
  }

  function handleTitleChange(key, value) {
    setDataCollection({ ...dataCollection, title: value });
  }

  function handleSubmit() {
    const empData = empClassesDataCollection.employeeClasses.map((emp) => {
      return {
        id: emp.id,
        totalCalculativeWage: emp.empSumHonorariumShare,
        totalCalculativeHours: emp.empSumHourShare,
      };
    });

    const data = {
      ...servicesDataCollection,
      ...dataCollection,
      wagePresets: empData,
      projectId: project.id,
      id:
        project.id == (dataCollection.projectId ?? project.id)
          ? dataCollection.id
          : null,
    };

    DBRequest({
      config,
      path: data.id ? `wage-calculations/${data.id}` : `wage-calculations`,
      method: data.id ? API_PATCH(data) : API_POST(data),
      onResponse: handleSubmitResponse,
    });
  }

  function handleSubmitResponse(res) {
    onSubmit(res);
  }

  return (
    <Box className="w-full">
      <Box className="pb-5 pt-3">
        <TypeText
          field={{ label: 'Title', key: 'title' }}
          dataCollection={dataCollection}
          updateValidationOnChange={handleTitleChange}
        />
      </Box>
      <table className="w-full">
        <colgroup>
          <col />
          {empClassesDataCollection.employeeClasses.map((el) => (
            <col
              key={'EmpClassCiolumns' + el.id}
              style={
                {
                  //background: `${el.color}55`,
                }
              }
            />
          ))}
        </colgroup>
        <tbody>
          <tr>
            <td>
              <Box className="w-[80%] py-2">
                <TypeNumber
                  field={{
                    label: t('Total Costs'),
                    key: 'totalCost',
                    unit: '€',
                  }}
                  dataCollection={dataCollection}
                  errors={{}}
                  updateValidationOnChange={handleNettoHonorariumChange}
                  size="small"
                />
              </Box>
            </td>
            {empClassesDataCollection.employeeClasses.map((empClass) => {
              return (
                <td
                  className="px-1"
                  key={'EmpClassMap' + empClass.id}
                  style={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}
                >
                  <EmployeeClass
                    empClass={empClass}
                    //actions={generateEmpClassActions(empClass)}
                  />
                </td>
              );
            })}
            <td className="px-1">
              <Box className="flex justify-center">
                <Typography>{t('Control')}</Typography>
              </Box>
              {/*<ProjectAddElementButton onClick={() => setOpenNewEmpClass({})} />*/}
            </td>
          </tr>
          {servicesDataCollection.services.map((service, index) => {
            return (
              <Fragment key={'ServiceMap' + service.id}>
                <tr>
                  <td>
                    <Service
                      service={service}
                      actions={generateServiceActions(service)}
                      toggleExpand={handleToggleExpand}
                    />
                  </td>
                  {empClassesDataCollection.employeeClasses.map((empClass) => {
                    return (
                      <td key={'MatrixData' + empClass.id}>
                        <Box className="flex flex-col justify-between gap-5 px-2">
                          {service.expanded && (
                            <Tooltip title={t('Service Share')}>
                              <Box className="flex justify-end">
                                <Typography fontSize={12} className="w-[105px]">
                                  {empClass &&
                                    service &&
                                    dataCollection?.data &&
                                    dataCollection.data.length > 0 && (
                                      <TypeNumber
                                        field={{
                                          //label: t('Service Share'),
                                          key: 'employeePercentPerPhase',
                                          unit: '%',
                                        }}
                                        dataCollection={{
                                          employeePercentPerPhase:
                                            dataCollection.data[
                                              `emp${empClass.id}serv${service.id}`
                                            ].employeePercentPerPhase,
                                        }}
                                        errors={{}}
                                        updateValidationOnChange={(
                                          key,
                                          value
                                        ) =>
                                          handleEmployeePercentPerPhaseChange(
                                            key,
                                            value,
                                            service,
                                            empClass
                                          )
                                        }
                                        size={'small'}
                                      />
                                    )}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {empClass &&
                            service &&
                            dataCollection?.data &&
                            dataCollection.data.length > 0 && (
                              <Box className="flex justify-between gap-2 items-center px-1 pt-1">
                                <Typography fontSize={12}>
                                  {(Math.round(
                                    dataCollection.data[
                                      `emp${empClass.id}serv${service.id}`
                                    ].hourShare * 100
                                  ) / 100 ?? 0) + 'h'}
                                </Typography>
                                <Typography>
                                  <MoneyLabel
                                    money={
                                      dataCollection.data[
                                        `emp${empClass.id}serv${service.id}`
                                      ].honorariumShare ?? 0
                                    }
                                  />
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      </td>
                    );
                  })}
                  <td>
                    {service.expanded && (
                      <Box className="flex flex-col items-center">
                        {/*<Typography>{t('Control') + ': '}</Typography>*/}
                        <Typography>
                          {(service.percentSum ?? 0) + '%'}
                        </Typography>
                      </Box>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="pt-2">
                    <Divider />
                  </td>
                  {empClassesDataCollection.employeeClasses.map((el) => (
                    <td key={'EmpClassDividers' + el.id} className="pt-2">
                      <Divider />
                    </td>
                  ))}
                  <td className="pt-2">
                    <Divider />
                  </td>
                </tr>
              </Fragment>
            );
          })}
          <tr>
            <td>
              {
                <ProjectAddElementButton
                  onClick={() => setOpenNewService({})}
                />
              }
            </td>
            {empClassesDataCollection.employeeClasses.map((empClass) => {
              return (
                <td
                  key={'EmpClassSum' + empClass.id}
                  className="p-2"
                  style={{
                    borderBottomLeftRadius: 10,
                    borderBottomRightRadius: 10,
                  }}
                >
                  <Box className="flex justify-between px-1 items-center">
                    <Typography fontWeight={600} fontSize={12}>
                      {Math.round((empClass.empSumHourShare ?? 0) * 100) / 100 +
                        'h'}
                    </Typography>
                    <Typography fontWeight={600}>
                      <MoneyLabel money={empClass.empSumHonorariumShare ?? 0} />
                    </Typography>
                  </Box>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>

      <Box className="w-full flex justify-between mt-5">
        <Button variant="contained" color="error" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={!dataCollection.title || dataCollection.totalCost == null}
        >
          {t('Accept')}
        </Button>
      </Box>

      <ExoDialog
        open={openNewEmpClass}
        onClose={() => setOpenNewEmpClass(null)}
      >
        {openNewEmpClass?.id ? (
          <ExoForm
            fields={newEmpFields}
            dataCollection={{}}
            startDataCollection={openNewEmpClass}
            onSubmit={handleAddEmpClass}
            onCancel={() => setOpenNewEmpClass(null)}
            noDifferences={true}
            validationSchema={newEmpValidationSchema}
          />
        ) : (
          <ExoVariants variants={newEmpClassVariants} />
        )}
      </ExoDialog>
      <ExoDialog open={openNewService}>
        {openNewService?.id ? (
          <ExoForm
            fields={newServiceFields}
            startDataCollection={{
              ...openNewService,
              useAbsolute: openNewService?.useAbsolute || '0',
              percentOfHonorarium: openNewService?.percentOfHonorarium || null,
              amount: openNewService?.amount || null,
            }}
            onSubmit={handleAddService}
            onCancel={() => {
              setOpenNewService(null);
            }}
            noDifferences={true}
            validationSchema={newServiceValidationSchema}
          />
        ) : (
          <ExoVariants variants={newServiceVariants} />
        )}
      </ExoDialog>
    </Box>
  );
};
